import {
  BaseQueryFn,
  EndpointBuilder,
  FetchArgs,
  FetchBaseQueryError,
  retry
} from "@reduxjs/toolkit/query/react";

import { datadogRum } from "@datadog/browser-rum";
import { isRejectedWithValue } from "@reduxjs/toolkit";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { isEqual } from "lodash";

import { RootState } from "app/store";
import {
  setAdditionalAction,
  setCustomMessage,
  setLabel,
  setOpen,
  setPendingCustomMessage
} from "components/action-snackbar/action-snackbar-slice";
import { logoutUser, setToken } from "features/auth/auth-slice";
import { buildAuthHeaderValue } from "utils/auth-header.util";
import { refreshBearerToken } from "utils/refresh-bearer-token.util";
import {
  MAX_TIMEOUT_RETRIES,
  getErrorMessage,
  getRequestMethod,
  isFetchError,
  isRetryableNetworkError,
  isTimeoutLikeError
} from "utils/services-errors.util";
import { viteEnv } from "utils/vite-env.util";

export const prepareHeaders = (
  headers: Headers,
  { getState, type }: { getState: any; type: "query" | "mutation" }
) => {
  const { token, authScheme } = (getState() as RootState).auth;

  if (token) {
    headers.set("Authorization", buildAuthHeaderValue(token, authScheme));
  }

  headers.set("X-Timezone", "practice");
  headers.set("x-atomic-request", "True");

  // Add X-Reports-DB header for GET requests from Reports and Dashboard pages
  if (type === "query") {
    const pathname = window.location.pathname;
    if (
      pathname.startsWith("/reports/") ||
      pathname.startsWith("/dashboard/")
    ) {
      headers.set("X-Reports-DB", "true");
    }
  }

  return headers;
};

// Update the persisted Bearer access token in whichever bucket holds it, so a
// reload after a silent refresh rehydrates the fresh token.
const persistBearerAccessToken = (access: string) => {
  if (localStorage.getItem("token") !== null) {
    localStorage.setItem("token", access);
  } else {
    sessionStorage.setItem("token", access);
  }
};

// Single-flight Bearer refresh: concurrent 401s share one refresh call instead
// of stampeding the refresh endpoint.
let refreshInFlight: Promise<string | null> | null = null;
const refreshBearerOnce = (baseUrl: string): Promise<string | null> => {
  if (!refreshInFlight) {
    refreshInFlight = refreshBearerToken(baseUrl)
      .then((r) => r?.access ?? null)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

const baseQueryFn = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders
  });

export const baseQueryWithReauthFn = (baseUrl: string | (() => string)) => {
  const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const resolvedBaseUrl = typeof baseUrl === "function" ? baseUrl() : baseUrl;

    // Retry transient timeouts on GET requests only. Some endpoints are
    // builder.query but POST (e.g. ai/visualization/ask) — retrying those
    // could duplicate expensive side effects. RTKQ's `type === 'mutation'`
    // alone is not enough; we must check the HTTP method.
    //
    // Note: RTKQ's `retry` only consults `retryCondition` to terminate the
    // loop — `maxRetries` is used for backoff delay clamping, not as a cap
    // (see node_modules/@reduxjs/toolkit/src/query/retry.ts). The cap must
    // be enforced inside the condition. We honor a per-endpoint
    // `extraOptions.maxRetries` override (e.g. ambient-scribe sets 1).
    const baseQueryWithRetry = retry(baseQueryFn(resolvedBaseUrl), {
      retryCondition: (
        error,
        requestArgs,
        { attempt, baseQueryApi, extraOptions: endpointExtraOptions }
      ) => {
        const cap =
          (endpointExtraOptions as { maxRetries?: number })?.maxRetries ??
          MAX_TIMEOUT_RETRIES;
        if (attempt > cap) return false;
        if (baseQueryApi.type === "mutation") return false;
        if (getRequestMethod(requestArgs) !== "GET") return false;
        return isRetryableNetworkError(error);
      }
    });

    let result = await baseQueryWithRetry(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
      // Endpoints whose 401 does not prove the Suno session is dead opt out
      // of the logout below (ENG-1966): the DialStack session mint runs on a
      // timer in the background, and one stray 401 from it must not sign the
      // user out mid-call. If the session really is gone, the next ordinary
      // request logs them out as before. The Bearer self-heal still runs.
      const keepSessionOn401 = (
        extraOptions as { keepSessionOn401?: boolean } | undefined
      )?.keepSessionOn401;
      const state = api.getState() as RootState;
      const token = state.auth.token;
      const authScheme = state.auth.authScheme;

      // Check if we're already on login page or an unauthorized route
      const isOnLoginPage =
        window.location.pathname === "/login" ||
        window.location.pathname === "/forgot-password" ||
        window.location.pathname.startsWith("/password-reset") ||
        window.location.pathname.startsWith("/activate");

      // Bearer (switched) sessions can self-heal: try the HttpOnly refresh
      // cookie once before tearing down. Token sessions have no refresh, so
      // they fall straight through to logout.
      if (token && !isOnLoginPage && authScheme === "Bearer") {
        const access = await refreshBearerOnce(resolvedBaseUrl);
        if (access) {
          persistBearerAccessToken(access);
          api.dispatch(setToken({ token: access, scheme: "Bearer" }));
          result = await baseQueryWithRetry(args, api, extraOptions);
          if (!(result.error && result.error.status === 401)) {
            return result; // recovered — no logout
          }
        }
      }

      // Only logout and show message if we have a token and are not on login page
      if (token && !isOnLoginPage && !keepSessionOn401) {
        console.log("401 error, logging out user");
        // Re-read the token: a failed Bearer refresh may have changed state.
        const currentToken = (api.getState() as RootState).auth.token;
        api.dispatch(logoutUser({ token: currentToken }));
        api.dispatch(
          setCustomMessage(
            "Session expired. Redirecting to login for re-authentication."
          )
        );
        api.dispatch(setOpen(true));
      }
    }
    if (
      result.error &&
      result.error.status === 403 &&
      (result.error.data as { message: string; code: string })?.message ===
        "Access Denied: Invalid IP"
    ) {
      const state = api.getState() as RootState;
      const token = state.auth.token;

      // Check if we're already on login page or an unauthorized route
      const isOnLoginPage =
        window.location.pathname === "/login" ||
        window.location.pathname === "/forgot-password" ||
        window.location.pathname.startsWith("/password-reset") ||
        window.location.pathname.startsWith("/activate");

      // Only logout and show message if we have a token and are not on login page
      if (token && !isOnLoginPage) {
        console.log("403 error: Access Denied: Invalid IP, logging out user");
        api.dispatch(logoutUser({ token }));
        api.dispatch(
          setCustomMessage(
            "Access Restricted: You're currently on an unauthorized network. Please reconnect from work or an approved location."
          )
        );
        api.dispatch(setOpen(true));
      }
    }
    if (
      result.error &&
      (isTimeoutLikeError(result.error) || isFetchError(result.error))
    ) {
      const state = api.getState() as RootState;
      const message = getErrorMessage(result.error);
      const url = typeof args === "string" ? args : args.url;

      // Distinct DD title per error type so monitoring/alerting isn't
      // mislabelled. Keep the title low-cardinality (no URL) so DD groups
      // by error type — the URL goes in context for facet filtering.
      // Strip query params for stable per-path grouping.
      // Wrap in try/catch in case RUM isn't initialized (local dev).
      const ddTitle = isFetchError(result.error)
        ? "API network error"
        : "API timeout";
      try {
        datadogRum.addError(new Error(ddTitle), {
          url: url?.split("?")[0],
          status: result.error.status,
          practiceId: state.auth.practice?.id
        });
      } catch {
        // RUM unavailable — swallow.
      }

      // Endpoints that render their own inline error (e.g. Noah-ES, ENG-1131) opt out of the
      // global toast via extraOptions to avoid double-messaging — RUM logging above still runs.
      const suppressGlobalToast = (
        extraOptions as { suppressGlobalErrorToast?: boolean } | undefined
      )?.suppressGlobalErrorToast;

      // Don't overwrite an active snackbar (e.g. "Saved successfully") —
      // queue the timeout message to fire after it closes. Without this,
      // background polling timeouts can silently disappear behind a
      // success toast, contradicting the ticket goal.
      if (!suppressGlobalToast) {
        if (state.snackbar?.open) {
          api.dispatch(setPendingCustomMessage(message));
        } else {
          // Clear stale metadata from a just-closed toast that hasn't been
          // cleaned up yet by AppSnackbar's effect — prevents an action
          // button from a previous toast leaking into our error toast.
          api.dispatch(setLabel(""));
          api.dispatch(setAdditionalAction(undefined));
          api.dispatch(setCustomMessage(message));
          api.dispatch(setOpen(true));
        }
      }
    }

    return result;
  };

  return baseQueryWithReauth;
};

type PaginatedResultType<ResultType> = {
  [page: number]: ResultType;
  lastFetchedPage: number;
};

type CreatePaginatedEndpointArgs<QueryArgType, ResultType> = {
  queryFn: (arg: QueryArgType) => string | FetchArgs;
  transformResponseFn: (
    response: any,
    meta?: unknown,
    arg?: QueryArgType
  ) => ResultType;
  providesTags?: any;
};

export function createPaginatedEndpoint<QueryArgType, ResultType>(
  builder: EndpointBuilder<
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    any,
    string
  >,
  {
    queryFn,
    transformResponseFn,
    providesTags
  }: CreatePaginatedEndpointArgs<QueryArgType, ResultType>
) {
  return builder.query<PaginatedResultType<ResultType>, QueryArgType>({
    query: queryFn,
    providesTags,
    transformResponse: (response: any, meta: unknown, arg: QueryArgType) => {
      const transformedResponse = transformResponseFn(response, meta, arg);
      const { limit = 1, offset = 0 } = arg as any; // Cast based on your QueryArgType
      const page = Math.round(offset / limit);
      return {
        [page]: transformedResponse,
        lastFetchedPage: page
      };
    },
    serializeQueryArgs: ({ queryArgs }) => {
      const { limit, offset, ...filters } = queryArgs as any; // Cast based on your QueryArgType
      return filters;
    },
    merge(currentCacheData, incomingData) {
      // A page may refresh a cached page or extend the list by one, never leave a gap.
      // usePaginationV2 trims the later pages when a list resets; a later-page request
      // still in flight at that moment (e.g. a tag-invalidation refetch of the scrolled-to
      // offset) must not land afterwards and sit after page 0 with the pages between missing.
      const incomingPages = Object.keys(incomingData)
        .filter((key) => key !== "lastFetchedPage")
        .map(Number);
      const firstIncomingPage = Math.min(...incomingPages);
      if (
        incomingPages.length > 0 &&
        firstIncomingPage > (currentCacheData?.lastFetchedPage ?? 0) + 1
      ) {
        return currentCacheData;
      }
      return {
        ...currentCacheData,
        ...incomingData,
        lastFetchedPage: Math.max(
          incomingData.lastFetchedPage,
          currentCacheData?.lastFetchedPage ?? 0
        )
      };
    },
    forceRefetch({ currentArg, previousArg }) {
      return !isEqual(currentArg, previousArg);
    }
  });
}

export const safeRefetch = <T>(
  refetch?: () => Promise<T> | void,
  isUninitialized?: boolean
): Promise<T | void> => {
  if (!refetch || isUninitialized) return Promise.resolve();
  try {
    const result = refetch();
    return result?.catch?.(() => {}) ?? Promise.resolve();
  } catch {
    return Promise.resolve();
  }
};

export const rtkQueryErrorLogger = () => (next: any) => (action: any) => {
  if (
    isRejectedWithValue(action) &&
    viteEnv.VITE_ENV !== "prod" &&
    viteEnv.VITE_ENV !== "preprod"
  ) {
    console.warn("RTK Query error:", action.payload);
  }
  return next(action);
};
